import { InvokeCommand, LambdaClient } from '@aws-sdk/client-lambda';

import config from '@/config';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-provider-cognito-identity';
import { logger } from '@/services/logger/cloudwatch';

export type HttpMethod = 'GET' | 'POST' | 'PUT';

export interface GuestLambdaClientOptions {
  lambdaArn: string;
  region: string;
  getHeaders?: () => Promise<Record<string, string>> | Record<string, string>;
}

export const createGuestLambdaClient = (options: GuestLambdaClientOptions) => {
  const { lambdaArn, region, getHeaders } = options;

  const lambdaClient = new LambdaClient({
    region,
    credentials: fromCognitoIdentityPool({
      clientConfig: { region },
      identityPoolId:
        config.services.aws.awsConfig.Auth?.Cognito.identityPoolId ?? '',
    }),
  });

  const request = async <T>(
    httpMethod: HttpMethod,
    path: string,
    body?: unknown,
  ): Promise<T> => {
    logger.debug(`guestLambdaRequest [${lambdaArn}]: ${httpMethod}`, {
      path,
      requestData: body,
    });

    const headers = getHeaders ? await getHeaders() : undefined;

    const payload = {
      httpMethod,
      path: `/${path}`,
      pathParameters: { path },
      ...(headers ? { headers } : {}),
      ...(body ? { body: JSON.stringify(body) } : {}),
    };

    const command = new InvokeCommand({
      FunctionName: lambdaArn,
      InvocationType: 'RequestResponse',
      Payload: JSON.stringify(payload),
    });

    const response = await lambdaClient.send(command);

    if (response.FunctionError) throw new Error(response.FunctionError);
    if (!response?.Payload) throw new Error('No response payload');

    logger.debug(
      `guestLambdaResponse [${lambdaArn}]: ${httpMethod}`,
      response.Payload,
    );

    const parsed = JSON.parse(
      new TextDecoder('utf-8').decode(response.Payload),
    );
    return parsed.body ? JSON.parse(parsed.body) : parsed;
  };

  return {
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body: unknown) => request<T>('POST', path, body),
    put: <T>(path: string, body: unknown) => request<T>('PUT', path, body),
  };
};
